import { type ChangeEvent, useCallback, useState } from "react";
import { toast } from "react-toastify";
import { toastOptions } from "../../tools/helpers/ToastHelper";
import { useTranslation } from "react-i18next";
import StorageEnum from "../../enums/Storage.enum";
import { lsLoad } from "../../tools/Storage";
import { useMarketComponentsDeleteOptimistic } from "../../apis/market-component/useMarketComponentsDeleteOptimistic.ts";
import { useEditMarket } from "../../provider/edit-market/EditMarket.hook.ts";
import { format } from "date-fns";
import {
  getMaxDaysBeforeSwitchToRed,
  getMaxStatusDays,
} from "../../tools/ConfigurationReader";
import type MarketComponentEditDialogProperties from "./MarketComponentEditDialog.properties.ts";
import type { MarketComponentArticleTypeMarketComponentArticleTypeOutput } from "../../generated/fresh-guard-api";

export const useMarketComponentEditDialogController = (
  props: MarketComponentEditDialogProperties,
) => {
  const { t } = useTranslation();
  const { updateMarketComponent } = useEditMarket();
  const marketComponentArticleTypes = lsLoad(
    StorageEnum.MarketComponentArticleTypes,
  );
  const [selectedValue, setSelectedValue] = useState(
    props.marketComponent?.articleTypeId,
  );
  const [number, setNumber] = useState(props.marketComponent?.number ?? 0);
  const [shortDescription, setShortDescription] = useState(
    props.marketComponent?.shortDescription ?? "",
  );
  const [switchToYellowAfterXDays, setSwitchToYellowAfterXDays] = useState(
    props.marketComponent?.switchToYellowAfterXDays ?? 0,
  );
  const [switchToRedAfterXDays, setSwitchToRedAfterXDays] = useState(
    props.marketComponent?.switchToRedAfterXDays ?? 0,
  );
  const [switchToRedAfterDate, setSwitchToRedAfterDate] = useState(
    props.marketComponent?.switchToRedAfterDate ?? null,
  );
  const [daysBeforeSwitchToRed, setDaysBeforeSwitchToRed] = useState(
    props.marketComponent?.daysBeforeSwitchToRed ?? 0,
  );
  const [orientation, setOrientation] = useState(
    props.marketComponent?.orientation ?? 0,
  );
  const { idsOfNewMarketComponents } = useEditMarket();

  const { mutate, isPending } = useMarketComponentsDeleteOptimistic();

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const articleType: MarketComponentArticleTypeMarketComponentArticleTypeOutput =
      marketComponentArticleTypes.find(
        (item: MarketComponentArticleTypeMarketComponentArticleTypeOutput) =>
          item.id === parseInt(event.target.value),
      );

    if (articleType) {
      setSwitchToYellowAfterXDays(articleType.switchToYellowAfterXDays ?? 0);
      setSwitchToRedAfterXDays(articleType.switchToRedAfterXDays ?? 0);
      setDaysBeforeSwitchToRed(articleType.daysBeforeSwitchToRed ?? 0);
    }

    setSelectedValue(parseInt(event.target.value));
  };

  const editOrientation = (value: string) => {
    const degree = parseInt(value);

    if (degree < 0 || degree > 359) {
      toast.error(t("error.invalidValueForRotate"), toastOptions);
    } else {
      setOrientation(parseInt(value));
    }
  };

  const editSwitchToYellowAfterXDays = (value: string) => {
    const days = parseInt(value);

    if (days < 0 || days > getMaxStatusDays()) {
      toast.error(
        t("error.invalidValueForSwitchToYellowAfterXDays"),
        toastOptions,
      );
    } else {
      setSwitchToYellowAfterXDays(parseInt(value));
    }
  };

  const editSwitchToRedAfterXDays = (value: string) => {
    const days = parseInt(value);

    if (days < 0 || days > getMaxStatusDays()) {
      toast.error(
        t("error.invalidValueForSwitchToRedAfterXDays"),
        toastOptions,
      );
    } else {
      setSwitchToRedAfterXDays(parseInt(value));
    }
  };

  const editDaysBeforeSwitchToRed = (value: string) => {
    const days = parseInt(value);

    if (days < 0 || days > getMaxDaysBeforeSwitchToRed()) {
      toast.error(
        t("error.invalidValueForDaysBeforeSwitchToRed"),
        toastOptions,
      );
    } else {
      setDaysBeforeSwitchToRed(parseInt(value));
    }
  };

  const editSwitchToRedAfterDate = (value: Date | null) => {
    if (value) {
      const dateAsString = format(value, t("date.technicalDate"));
      setSwitchToRedAfterDate(dateAsString);
    } else {
      setSwitchToRedAfterDate(null);
    }
  };

  const handleNumberChange = (value: string) => {
    if (!isNaN(parseInt(value))) {
      setNumber(parseInt(value));
      setShortDescription(value);
    }
  };

  const handleSaveClick = () => {
    if (props.marketComponent) {
      if (
        props.marketComponent.textLength &&
        shortDescription.length > props.marketComponent.textLength
      ) {
        toast.error(
          t("apiError.TEXT_LENGTH_FOR_SHORT_DESCRIPTION_TOO_LONG"),
          toastOptions,
        );
        return;
      }

      const updatedMarketComponent = {
        ...props.marketComponent,
        number,
        orientation,
        shortDescription,
        articleTypeId: selectedValue,
        switchToYellowAfterXDays,
        switchToRedAfterXDays,
        daysBeforeSwitchToRed,
        switchToRedAfterDate: switchToRedAfterDate?.toString(),
      };

      updateMarketComponent(updatedMarketComponent);
    }

    props.handleDialogCloseClick();
  };

  const removeMarketComponent = useCallback(() => {
    props.removeMarketComponent(props.marketComponent);
    props.handleDialogCloseClick();
    toast.success(t("success.marketComponentDeleted"), toastOptions);
  }, [t, props]);

  const handleDeleteClick = () => {
    const found = idsOfNewMarketComponents.find(
      (id: number) =>
        props.marketComponent?.id && id === props.marketComponent.id,
    );

    if (found) {
      removeMarketComponent();
    } else {
      if (props.marketComponent?.id) {
        mutate(
          { marketComponentId: props.marketComponent.id.toString() },
          {
            onSuccess: () => {
              removeMarketComponent();
            },
          },
        );
      }
    }
  };

  return {
    orientation,
    number,
    shortDescription,
    marketComponentArticleTypes,
    selectedValue,
    switchToYellowAfterXDays,
    switchToRedAfterXDays,
    switchToRedAfterDate,
    daysBeforeSwitchToRed,
    loading: isPending,
    handleChange,
    editSwitchToYellowAfterXDays,
    editSwitchToRedAfterXDays,
    editSwitchToRedAfterDate,
    editDaysBeforeSwitchToRed,
    setShortDescription,
    handleNumberChange,
    editOrientation,
    handleSaveClick,
    handleDeleteClick,
  };
};
